import { useState, useEffect, useMemo } from 'react';
import { ReclaimProofRequest } from '@reclaimprotocol/js-sdk';
import './App.css';

// --- Reusable Chart & Display Components ---

const FollowerDonutChart = ({ followerPercent, nonFollowerPercent }) => {
  if (followerPercent === undefined || nonFollowerPercent === undefined) return null;

  const followerP = Math.round(followerPercent * 100);
  const conicGradient = `conic-gradient(var(--accent-cyan) 0% ${followerP}%, var(--accent-pink) ${followerP}% 100%)`;

  return (
    <div className="metric-card">
      <div className="metric-title">Follower Views</div>
      <div className="donut-chart-container">
        <div className="donut-chart" style={{ background: conicGradient }}></div>
        <div className="donut-legend">
          <div className="legend-item"><span className="legend-color" style={{ background: 'var(--accent-cyan)' }}></span>Followers: <b>{followerP}%</b></div>
          <div className="legend-item"><span className="legend-color" style={{ background: 'var(--accent-pink)' }}></span>Non-Followers: <b>{100 - followerP}%</b></div>
        </div>
      </div>
    </div>
  );
};

const RetentionChart = ({ data, duration }) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="metric-card" style={{ textAlign: 'left' }}>
      <div className="metric-title">Viewer Retention</div>
      <div className="retention-chart-bars">
        {data.map((point, index) => (
          <div 
            key={index} 
            className="retention-bar" 
            style={{ height: `${point.value * 100}%` }}
            title={`${(point.value * 100).toFixed(0)}% retention at ${(point.timestamp / 1000).toFixed(1)}s`}
          />
        ))}
      </div>
      <div className="retention-labels">
        <span>0s</span>
        <span>{(duration / 2000).toFixed(1)}s</span>
        <span>{(duration / 1000).toFixed(1)}s</span>
      </div>
    </div>
  );
};

const DistributionDisplay = ({ title, data, valueKey = 'value', nameKey = 'key' }) => {
  if (!data || data.length === 0 || data.every(item => (item[valueKey] * 100) < 1)) {
    return null;
  }
  return (
    <div className="metric-card" style={{ textAlign: 'left' }}>
      <div className="metric-title" style={{ marginBottom: '1rem' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {data.map((item, index) => {
          const percentage = (item[valueKey] * 100).toFixed(1);
          if (parseFloat(percentage) < 1) return null;
          
          let name = item[nameKey];
          if (name === 'male_vv') name = 'Male';
          if (name === 'female_vv') name = 'Female';

          return (
            <div key={index} className="distribution-item">
              <div className="distribution-item-header">
                <span>{name}</span>
                <span style={{ fontWeight: '500' }}>{percentage}%</span>
              </div>
              <div className="distribution-bar-bg">
                <div className="distribution-bar-fg" style={{ width: `${percentage}%`, background: 'linear-gradient(90deg, var(--accent-pink), var(--accent-cyan))' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Individual Post Card ---

const ProofDisplay = ({ proof }) => {
  try {
    const claimData = proof?.claimData || {};
    let context = {};
    let extractedParams = {};
    let postData = {};
    
    // Safely parse context
    if (claimData.context) {
      try {
        context = JSON.parse(claimData.context);
        extractedParams = context.extractedParameters || {};
      } catch (e) {
        console.error('Error parsing context:', e);
      }
    }
    
    // Safely parse post data
    const postDataString = extractedParams.post || null;
    if (postDataString) {
      try {
        postData = JSON.parse(postDataString);
      } catch (e) {
        console.error('Error parsing post data:', e);
        // Try to extract just the video_info if the full post data fails
        if (extractedParams.video_info) {
          postData = { video_info: extractedParams.video_info };
        }
      }
    }
    
    const videoInfo = postData?.video_info || {};
    const stats = videoInfo?.statistics || {};
    const timestamp = claimData.timestampS 
      ? new Date(claimData.timestampS * 1000).toLocaleString()
      : new Date().toLocaleString();
    

    const avgWatchTime = postData?.realtime_average_watch_time_history?.total?.toFixed(2);
    const retentionData = postData?.video_retention_rate_realtime?.value?.list;
    const trafficSources = postData?.video_traffic_source_percent_realtime?.value?.value;

    return (
      <div className="proof-card">
        <div className='card-header'>
          <div className="header-stats">
            <div className="header-stat-item">
              <div className="metric-title">Views</div>
              <div className="metric-value">{stats.play_count?.toLocaleString() ?? 'N/A'}</div>
            </div>
            <div className="header-stat-item">
              <div className="metric-title">❤️ Likes</div>
              <div className="metric-value" style={{color: 'var(--accent-pink)'}}>{stats.digg_count?.toLocaleString() ?? 'N/A'}</div>
            </div>
            <div className="header-stat-item">
              <div className="metric-title">💬 Comments</div>
              <div className="metric-value">{stats.comment_count?.toLocaleString() ?? 'N/A'}</div>
            </div>
            <div className="header-stat-item">
              <div className="metric-title">🔗 Shares</div>
              <div className="metric-value">{stats.share_count?.toLocaleString() ?? 'N/A'}</div>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="card-section">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <RetentionChart data={retentionData} duration={videoInfo.video?.duration} />
              <DistributionDisplay title="Traffic Sources" data={trafficSources} />
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering proof data:', error, { proof });
    return <div className="error">Failed to display proof. See console for details.</div>;
  }
};

// --- Aggregate Overview Dashboard ---

const DashboardOverview = ({ proofs }) => {
  const totals = useMemo(() => {
    if (!Array.isArray(proofs)) return { views: 0, likes: 0, comments: 0, shares: 0 };

    return proofs.reduce((acc, proof) => {
      try {
        const context = JSON.parse(proof.claimData.context);
        const extractedParams = context.extractedParameters || {};
        let postData = {};
        
        // Safely parse post data
        if (extractedParams.post) {
          try {
            postData = JSON.parse(extractedParams.post);
          } catch (e) {
            console.error('Error parsing post in dashboard:', e);
            return acc;
          }
        }
        
        const stats = postData?.video_info?.statistics || {};
        acc.views += stats.play_count || 0;
        acc.likes += stats.digg_count || 0;
        acc.comments += stats.comment_count || 0;
        acc.shares += stats.share_count || 0;
        return acc;
      } catch (e) {
        console.error('Error processing proof in dashboard:', e);
        return acc;
      }
    }, { views: 0, likes: 0, comments: 0, shares: 0 });
  }, [proofs]);

  return (
    <div className="proof-section">
      <h2 style={{ marginBottom: '1.5rem' }}>Key Metrics Overview</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
        <div className="metric-card"><div className="metric-title">Total Video Views</div><div className="metric-value">{totals.views.toLocaleString()}</div></div>
        <div className="metric-card"><div className="metric-title">Total Likes</div><div className="metric-value" style={{color: 'var(--accent-pink)'}}>{totals.likes.toLocaleString()}</div></div>
        <div className="metric-card"><div className="metric-title">Total Comments</div><div className="metric-value" style={{color: 'var(--accent-cyan)'}}>{totals.comments.toLocaleString()}</div></div>
        <div className="metric-card"><div className="metric-title">Total Shares</div><div className="metric-value">{totals.shares.toLocaleString()}</div></div>
      </div>
    </div>
  );
}

// --- Main App Component ---

function App() {
  const [reclaimProofRequest, setReclaimProofRequest] = useState(null);
  const [proofs, setProofs] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [awemeId, setAwemeId] = useState('');
  const [fetchAllPosts, setFetchAllPosts] = useState(true);

  // ✅ CORRECTED INITIALIZATION LOGIC
  useEffect(() => {
    async function initializeReclaim() {
      try {
        const APP_ID = "0xde628892895CAE3852a8a78994184953a6A032e6";
        const APP_SECRET = "0x0228fdc803e8bcc36a9003de0046f62c65189e1e71e0118bd81cd917c425c319";
        
        const ALL_POSTS_PROVIDER_ID = 'cd397d9f-a781-4c62-94f5-0991ed3848f7';
        const SPECIFIC_POST_PROVIDER_ID = '9fa8c99b-4b28-499a-99bc-79c21e136ae8';
        
        const providerId = fetchAllPosts ? ALL_POSTS_PROVIDER_ID : SPECIFIC_POST_PROVIDER_ID;

        const proofRequest = await ReclaimProofRequest.init(
          APP_ID,
          APP_SECRET,
          providerId,
        );

        setReclaimProofRequest(proofRequest);
      } catch (err) {
        setError('Failed to initialize Reclaim SDK: ' + err.message);
        console.error(err);
      }
    }
    initializeReclaim();
  }, [fetchAllPosts]);

  async function handleCreateClaim() {
    if (!reclaimProofRequest) return;
    if (!fetchAllPosts && !awemeId.trim()) {
      setError('Please enter a TikTok post ID (aweme_id)');
      return;
    }

    setIsLoading(true);
    setError('');
    setProofs(null);

    try {
      if (!fetchAllPosts) {
        reclaimProofRequest.setParams({ aweme_id: awemeId });
      }

      reclaimProofRequest.setModalOptions({
        title: 'Prove TikTok Analytics',
        description: 'Scan the QR code to securely and privately prove your analytics data.',
        onClose: () => setIsLoading(false) 
      });

      await reclaimProofRequest.triggerReclaimFlow();

      await reclaimProofRequest.startSession({
        onSuccess: (receivedProofs) => {
          setIsLoading(false);
          setProofs(receivedProofs?.session?.proofs || receivedProofs);
        },
        onError: (err) => {
          setIsLoading(false);
          setError(`Verification failed: ${err.message}`);
        }
      });
    } catch (err) {
      setIsLoading(false);
      setError(`Failed to create claim: ${err.message}`);
    }
  }
  
  // Omitted handleCopyProof for brevity, as it's not part of the core functionality

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1 className="title">TikTok Analytics Dashboard</h1>
          <p className="subtitle">Generate verifiable proofs of your post metrics using Reclaim Protocol</p>
        </header>

        <section className="control-panel">
          <div style={{ marginBottom: '1.5rem', background: 'var(--background-tertiary)', padding: '1rem', borderRadius: '8px' }}>
            <label>
              <input
                type="checkbox"
                checked={fetchAllPosts}
                onChange={(e) => {
                  setFetchAllPosts(e.target.checked);
                  setProofs(null);
                }}
                style={{ marginRight: '0.75rem', transform: 'scale(1.2)', accentColor: 'var(--accent-pink)' }}
              />
              Generate Full Dashboard (All Recent Posts)
            </label>
          </div>
          
          {!fetchAllPosts && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="aweme-id">Single TikTok Post ID (aweme_id)</label>
              <input id="aweme-id" type="text" value={awemeId} onChange={(e) => setAwemeId(e.target.value)} placeholder="e.g., 7234567890123456789" />
            </div>
          )}

          <button className="button" onClick={handleCreateClaim} disabled={isLoading || !reclaimProofRequest || (!fetchAllPosts && !awemeId.trim())}>
            {isLoading ? <><span className="spinner" />Verifying with Reclaim...</> : '✨ Generate Verifiable Dashboard'}
          </button>
        </section>

        {error && <div className="error">{error}</div>}

        {proofs && (
          <>
            {Array.isArray(proofs) && <DashboardOverview proofs={proofs} />}
            <div className="proof-section">
              <h2 style={{ borderTop: '1px solid var(--border-color)', paddingTop: '2rem', marginTop: '2rem' }}>
                Content Breakdown
              </h2>
              <div className="proofs-grid">
                {Array.isArray(proofs) ? (
                  proofs.map(proof => <ProofDisplay key={proof.identifier} proof={proof} />)
                ) : (
                  <ProofDisplay proof={proofs} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default App;